import { z } from 'zod';

export const envelopeSchema = <K extends string, T extends z.ZodTypeAny>(key: K, dataSchema: T) =>
  z.object({ [key]: dataSchema } as Record<K, T>);

export const oauthConfigSchema = z.object({
  tenantId: z.string().min(1),
  clientId: z.string().min(1),
  redirectUri: z.string().url(),
});

export const authUserSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
});

export const baseEntitySchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
});

export const employeeSimpleSchema = z.object({
  employee_id: z.string(),
  name: z.string(),
});

export const employeeSchema = z
  .object({
    id: z.string(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    created_by: z.string().optional(),
    updated_by: z.string().optional(),
    employee_id: z.string(),
    fullname: z.string().optional(),
    name: z.string().optional(),
    email: z.string().min(1),
    role: z.string().optional(),
    status: z.string().optional(),
    location: z.string().optional(),
    time_model: z.string().optional(),
  })
  .transform((row) => ({
    ...row,
    fullname: row.fullname ?? row.name ?? '',
  }));

// More lenient employee schema for nested use (e.g., in employeeWorkingModelSchema)
// This allows invalid emails since nested employee objects may have incomplete data
export const nestedEmployeeSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  fullname: z.string().optional(),
  email: z.string().optional(), // Accept any string, don't validate email format for nested objects
  role: z.string().optional(),
  status: z.string().optional(),
  location: z.string().optional(),
  time_model: z.string().optional(),
});

export const locationSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  name: z.string(),
  office_head: z.string().optional().nullable(),
});

export const projectSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  project_number: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  manager: z.string().optional().nullable(),
  assistant: z.string().optional().nullable(),
  deputy: z.string().optional().nullable(),
  finance: z.string().optional().nullable(),
  status: z.string().optional(),
  portfolio: z.string().optional().nullable(),
  process_technology: z.string().optional().nullable(),
  delivery_model: z.string().optional().nullable(),
  start_date: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? '' : v)),
  end_date: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v == null || v === undefined ? '' : v)),
});

export const orderSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  order_number: z.string(),
  project_id: z.string(),
  project: projectSchema.optional(),
  comments: z.string().optional().nullable(),
  order_date: z.string().nullable(),
});

export const wbsSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  task_code: z.string(),
  project_type: z.string(),
  name: z.string().optional().nullable(),
  billable: z.boolean().optional(),
});

export const workingTimeModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  name: z.string(),
  fte: z.number().optional(),
  hours: z.number().optional(),
  monday: z.number().optional(),
  tuesday: z.number().optional(),
  wednesday: z.number().optional(),
  thursday: z.number().optional(),
  friday: z.number().optional(),
  saturday: z.number().optional(),
  sunday: z.number().optional(),
});

export const locationModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  location_id: z.string(),
  location: locationSchema.optional(),
  date_of_year: z.string(),
  not_working_percentage: z.number().optional(),
});

export const employeeWorkingModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  employee: nestedEmployeeSchema.optional(),
  working_time_model_id: z.string(),
  working_time_model: workingTimeModelSchema.optional(),
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string().optional(),
});

export const employeeLocationModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  employee: employeeSchema.optional(),
  location_id: z.string(),
  location: locationSchema.optional(),
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string().optional(),
});

export const employeeProjectModelSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  employee_id: z.string(),
  employee: nestedEmployeeSchema.optional(),
  project_id: z.string(),
  project: projectSchema.optional(),
  sales_rate: z.number(),
  work_type: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string().optional(),
  order: z.string().optional(),
});

export const workingTimeDaysModelSchema = z.object({
  monday: z.number(),
  tuesday: z.number(),
  wednesday: z.number(),
  thursday: z.number(),
  friday: z.number(),
  saturday: z.number(),
  sunday: z.number(),
});

export const employeeWorkingModelsDetailSchema = z.object({
  id: z.string(),
  working_time_model_id: z.string(),
  name: z.string(),
  hours: z.number(),
  working_time_model: workingTimeDaysModelSchema,
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string(),
});

export const employeeLocationModelsDetailSchema = z.object({
  id: z.string(),
  location_id: z.string(),
  location: z.object({
    id: z.string(),
    name: z.string(),
    office_head: z.string().optional(),
  }),
  start_date: z.string(),
  end_date: z.string(),
  no_end_date: z.boolean().optional(),
  status: z.string(),
});

export const organisationStructureSchema = z.object({
  id: z.union([z.number(), z.string()]).transform(String),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  department_no: z.string().optional().nullable(),
  department_name: z.string().optional().nullable(),
  team_no: z.string().optional().nullable(),
  team_name: z.string().optional().nullable(),
  employee_no: z.string().optional().nullable(),
  employee_name: z.string().optional().nullable(),
  employee_email: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  special_role: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  department_email: z.string().optional().nullable(),
  team_email: z.string().optional().nullable(),
  department_pk: z.string().optional().nullable(),
  team_pk: z.string().optional().nullable(),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
});

export const vendorSchema = z.object({
  id: z.union([z.number(), z.string()]).transform(String),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  vendor_id: z.string().optional().nullable(),
  vendor_name: z.string().optional().nullable(),
  vendor_type: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  postal_code: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  street: z.string().optional().nullable(),
  street_no: z.string().optional().nullable(),
});

// admapp.[role] -- controls who may sign in and who is an administrator.
export const appRoleSchema = z.object({
  id: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  user_name: z.string(),
  role: z.string(),
});

export const customerSchema = z.object({
  id: z.union([z.number(), z.string()]).transform(String),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  customer_id: z.string().optional().nullable(),
  customer_name: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  postal_code: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  street: z.string().optional().nullable(),
  street_no: z.string().optional().nullable(),
});

export const wbsDetailsSchema = z.object({
  id: z.string(),
  wbsNoLevel5: z.string().optional().nullable(),
  wbsNameLevel5: z.string().optional().nullable(),
  wbsNoLevel6: z.string().optional().nullable(),
  wbsNameLevel6: z.string().optional().nullable(),
});

export const locationModelDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  location_id: z.string(),
  date_of_year: z.string(),
  not_working_percentage: z.number(),
  location: z
    .object({
      id: z.string(),
      name: z.string(),
      office_head: z.string().optional(),
    })
    .optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
});
